import {sendEvent, stringifyObjectValues} from '@github-ui/hydro-analytics'
import {loaded} from '@github-ui/document-ready'
import {isAutomatedSession} from '@github-ui/stats'
import type {WebVitalMetric, MetricOrHPC} from './web-vitals'
import type {HPCTimingData} from './hpc-events'
import {getCPUBucket} from '@github-ui/cpu-bucket'
import type {INPAttribution} from './inp/metric'
import {getLCPBreakdown} from './utils/lcp-breakdown'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {
  __resetHydroPageContextForTesting,
  advanceHydroPageview,
  captureHydroPageContext,
  type HydroPageContext,
} from './utils/hydro-page-context'

interface WebVitalInformation {
  name: string
  value: number
  element?: string
  events?: string
  interactionType?: string
  eventType?: string
  elementType?: string
  inputDelay?: number
  processingDuration?: number
  presentationDelay?: number
  breakdown?: PlatformLCPBreakdown
}

interface HPCInformation extends WebVitalInformation {
  app: HPCTimingData['app']
  mechanism: HPCTimingData['mechanism']
  soft: boolean
}

/**
 * Container Timing companion to `hpc` on the same Hydro row. Mirrors the
 * HPC field shape (mechanism, gqlFetched, jsFetched, lazy, alternate)
 * so the two metrics can be diffed and faceted with the same Kusto
 * queries. The delta to HPC isn't precomputed on the client \u2014 Container
 * Timing usually fires earlier than HPC's LCP-finalised reporter, so
 * the precomputed value would be undefined in the common case. Kusto
 * subtracts `containerTiming.value - hpc.value` row-wise instead.
 */
interface ContainerTimingInformation {
  name: 'ContainerTiming'
  value: number
  identifier: string
  element?: string
  lastPaintedSubElement?: string
  size: number
  soft: boolean
  final: boolean
  firstRenderTime?: number
  lastPaintTime?: number
  app?: string
  mechanism?: HPCTimingData['mechanism']
  gqlFetched?: boolean
  jsFetched?: boolean
  lazy?: boolean
  alternate?: boolean
  domNodes?: number
  previousDomNodes?: number
  /**
   * True when the value was reported by the emulated-timeout fallback
   * (no terminal paint within `EMULATED_TIMEOUT` of the last
   * intermediate). Mirrors HPC's "emulated HPC" semantic so Kusto can
   * separate real-terminal vs fallback samples.
   */
  emulated?: boolean
}

interface HydroStat {
  app?: string
  dataRouter?: boolean
  react?: boolean
  reactApp?: string | null
  reactPartials?: string[]
  featureFlags?: string[]
  ssr?: boolean
  automated?: boolean
  hpc?: HPCInformation
  containerTiming?: ContainerTimingInformation
  ttfb?: WebVitalInformation
  fcp?: WebVitalInformation
  lcp?: WebVitalInformation
  fid?: WebVitalInformation
  inp?: WebVitalInformation
  cls?: WebVitalInformation
  elementtiming?: WebVitalInformation
  longTasks?: PerformanceEntryList
  longAnimationFrames?: PerformanceEntryList
  controller?: string
  action?: string
  routePattern?: string
  routeId?: string
  cpu?: string
  domNodes?: number
  previousDomNodes?: number
  navigationId?: string
}

interface HydroBatch {
  stat: HydroStat
  sent: boolean
}

const queued = new Map<number, HydroBatch>()
let observingSoftNavigations = false

export function __resetHydroForTesting() {
  queued.clear()
  __resetHydroPageContextForTesting()
}

export function observeHydroSoftNavigations() {
  if (observingSoftNavigations) return

  observingSoftNavigations = true
  ssrSafeDocument?.addEventListener(SOFT_NAV_STATE.START, advanceHydroPageview)
  ssrSafeDocument?.addEventListener(SOFT_NAV_STATE.RENDER, () => {
    const pageContext = captureHydroPageContext()
    const batch = queued.get(pageContext.id)
    if (batch) setPageContext(batch.stat, pageContext)
  })
}

/**
 * Lazily initialise the per-pageview batch with route + session metadata.
 * Called by every Hydro-bound reporter (HPC, web vitals, long tasks,
 * Container Timing). The first call seeds metadata from the same cached app
 * context used by browser stats. A different app or navigation starts a new
 * batch so an idle callback cannot merge source and destination pageviews.
 */
function ensureHydroStat({
  ssr,
  pageContext = captureHydroPageContext(),
  domNodes,
  previousDomNodes,
}: {
  ssr: boolean
  pageContext?: HydroPageContext
  domNodes?: number
  previousDomNodes?: number
}): HydroStat {
  const hydroStat = queueStat(pageContext)
  if (hydroStat.react === undefined) {
    setPageContext(hydroStat, pageContext)
    hydroStat.ssr = ssr
    hydroStat.cpu = getCPUBucket()
    hydroStat.automated = isAutomatedSession()
  }

  if (domNodes) hydroStat.domNodes = domNodes
  if (previousDomNodes) hydroStat.previousDomNodes = previousDomNodes
  return hydroStat
}

/**
 * Batched report of vital to hydro
 */
export function sendToHydro({
  metric,
  ssr,
  pageContext,
  domNodes,
  previousDomNodes,
  longTasks,
  longAnimationFrames,
}: {
  metric?: MetricOrHPC
  ssr: boolean
  pageContext?: HydroPageContext
  domNodes?: number
  previousDomNodes?: number
  longTasks?: PerformanceEntryList
  longAnimationFrames?: PerformanceEntryList
}) {
  const hydroStat = ensureHydroStat({ssr, pageContext, domNodes, previousDomNodes})

  if (metric) {
    return sendWebVital(hydroStat, metric)
  }

  hydroStat.longTasks = longTasks
  hydroStat.longAnimationFrames = longAnimationFrames
}

/**
 * Same-page companion to `sendToHydro` for Container Timing. Reuses the
 * shared per-pageview batch so HPC and Container Timing end up on the
 * same Hydro event row, joined implicitly. Kusto computes the diff
 * `containerTiming.value - hpc.value` row-wise.
 *
 * First-wins by `identifier`: the first `'hpc'`-identified container is
 * authoritative for that pageview. Subsequent reports (a nested misuse,
 * or a sub-container that also paints) are silently dropped here so a
 * stray inner container can't overwrite the page's primary signal. The
 * dupes are still visible in Datadog under the same `identifier: hpc`
 * tag if drift needs investigating. Non-`'hpc'` identifiers skip Hydro
 * entirely — they're not directly comparable to HPC and shouldn't
 * pollute the primary row.
 */
export function sendContainerTimingToHydro(info: ContainerTimingInformation, ssr: boolean) {
  if (info.identifier !== 'hpc') return

  const hydroStat = ensureHydroStat({ssr, pageContext: captureHydroPageContext(info.app)})
  if (hydroStat.containerTiming) return
  hydroStat.containerTiming = info
}

function sendWebVital(hydroStat: HydroStat, metric: MetricOrHPC) {
  if (metric.value < 60_000) {
    if (metric.name === 'HPC') {
      hydroStat[metric.name.toLocaleLowerCase() as Lowercase<typeof metric.name>] = buildHPCInformation(metric)
    } else {
      hydroStat[metric.name.toLocaleLowerCase() as Lowercase<typeof metric.name>] = buildWebVitalInformation(metric)
    }
  }
}

function buildHPCInformation(metric: HPCTimingData): HPCInformation {
  return {
    name: metric.name,
    value: metric.value,
    element: metric.attribution?.element,
    app: metric.app,
    soft: !!metric.soft,
    mechanism: metric.mechanism,
  }
}

function buildWebVitalInformation(metric: WebVitalMetric): WebVitalInformation {
  const vitalInformation: WebVitalInformation = {
    name: metric.name,
    value: metric.value,
  }

  switch (metric.name) {
    case 'LCP':
      vitalInformation.breakdown = getLCPBreakdown(metric)
      vitalInformation.element = metric.attribution?.target
      break
    case 'ElementTiming':
      vitalInformation.element = metric.attribution?.target
      break
    case 'INP':
      vitalInformation.element = metric.attribution?.interactionTarget
      // Only include custom fields if they exist (from our custom INPMetric class)
      if (metric.attribution && 'interactionType' in metric.attribution) {
        const customAttribution = metric.attribution as INPAttribution
        vitalInformation.interactionType = customAttribution.interactionType
        vitalInformation.eventType = customAttribution.eventType
        vitalInformation.inputDelay = customAttribution.inputDelay
        vitalInformation.processingDuration = customAttribution.processingDuration
        vitalInformation.presentationDelay = customAttribution.presentationDelay
      }
      if (metric.entries?.length) vitalInformation.events = metric.entries.map(entry => entry.name).join(',')
      break
    case 'CLS':
      vitalInformation.element = metric.attribution?.largestShiftTarget
      break
  }

  return vitalInformation
}

/**
 * Return the open batch for this pageview, regardless of report order.
 * Once its idle callback sends and removes the batch, a later report for the
 * same pageview starts a follow-up event because sent Hydro rows are immutable.
 */
function queueStat(pageContext: HydroPageContext): HydroStat {
  let batch = queued.get(pageContext.id)
  if (!batch) {
    batch = {stat: {}, sent: false}
    queued.set(pageContext.id, batch)
    scheduleSend(pageContext.id, batch)
  }
  return batch.stat
}

function setPageContext(hydroStat: HydroStat, pageContext: HydroPageContext) {
  hydroStat.app = pageContext.app
  hydroStat.react = pageContext.react
  hydroStat.reactApp = pageContext.reactApp
  hydroStat.dataRouter = pageContext.dataRouter
  hydroStat.reactPartials = pageContext.reactPartials
  hydroStat.featureFlags = pageContext.featureFlags
  hydroStat.controller = pageContext.controller
  hydroStat.action = pageContext.action
  hydroStat.routePattern = pageContext.routePattern
  hydroStat.routeId = pageContext.routeId
  hydroStat.navigationId = pageContext.navigationId
}

/**
 * Schedule a send to hydro
 */
async function scheduleSend(pageviewId: number, batch: HydroBatch) {
  await loaded
  window.requestIdleCallback(() => send(pageviewId, batch))
}

/**
 * Send the queued event to hydro
 */
function send(pageviewId: number, batch: HydroBatch) {
  if (batch.sent) return

  batch.sent = true
  sendEvent('web-vital', stringifyObjectValues(batch.stat))
  if (queued.get(pageviewId) === batch) queued.delete(pageviewId)
}
